import Color from '@/Enums/Color.enum';
import type { IDoohFilterVenueType } from '@/Interfaces/Forecaster/Dooh.interface';
import { PRIMARY } from '@/constants/color';
import { FORECASTER_DOOH_VENUE_TYPE_CHIPS_LIMIT } from '@/constants/forecaster';
import { TOOLTIP_LIST_LIMIT } from '@/constants/geography';
import { buildVenueTypesFilterTree, getAllVenueTypesFilterTreeChildren } from '@/helpers/forecaster/venueTypes';
import type { FilterVenueTypesNode } from '@/types/Forecaster/VenueNode.types';
import { ExpandMore } from '@mui/icons-material';
import { Box, ButtonBase, Chip, ClickAwayListener, Paper, Popper, Tooltip } from '@mui/material';
import { styled } from '@mui/material/styles';
import type { MouseEvent } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import VenueTypeHeader from './VenueTypeHeader';
import VenueTypePanel from './VenueTypePanel';

const Button = styled(ButtonBase)({
  fontSize: 16,
  width: '100%',
  textAlign: 'left',
  border: '1px solid #DEDEDE',
  color: '#757988',
  fontWeight: 500,
  justifyContent: 'space-between',
  padding: '12px 16px',
  height: 47,
  '& span': {
    width: '100%',
  },
  '& svg': {
    width: 16,
    height: 16,
  },
  ':focus-visible': {
    outline: '1px auto',
  },
});

const StyledPopper = styled(Popper)(({ theme }) => ({
  marginTop: 10,
  border: '1px solid #DCDCDE',
  borderRadius: 8,
  boxShadow: '0 10px 10px 0 rgb(16 10 85 / .1)',
  zIndex: theme.zIndex.modal,
  fontSize: 13,
  color: '#c9d1d9',
  backgroundColor: '#1c2128',
  ...theme.applyStyles('light', {
    color: '#24292e',
    backgroundColor: '#fff',
  }),
}));

const venueIdsField = 'Dooh.venueIds';

interface IProps {
  filterVenueTypes: IDoohFilterVenueType[];
}

const VenueType = ({ filterVenueTypes }: IProps) => {
  const [shouldUpdate, setShouldUpdate] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const { setValue, getValues } = useFormContext();
  const [selectedVenueType, setSelectedVenueType] = useState<IDoohFilterVenueType[]>([]);
  const [showAllChips, setShowAllChips] = useState<boolean>(false);

  useEffect(() => {
    const selectedIds = new Set(getValues(venueIdsField));
    if (filterVenueTypes) {
      setSelectedVenueType(filterVenueTypes?.filter(venue => selectedIds.has(venue.id)));
    }
  }, [filterVenueTypes]);

  const venueTypesTree = useMemo(() => buildVenueTypesFilterTree(filterVenueTypes), [filterVenueTypes]);

  const open = Boolean(anchorEl);

  const handleClick = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    if (anchorEl) {
      anchorEl.focus();
    }
    setAnchorEl(null);
    if (shouldUpdate) {
      setValue(
        venueIdsField,
        selectedVenueType.map(venue => venue.id),
      );
      setShouldUpdate(false);
    }
  }, [anchorEl, selectedVenueType, shouldUpdate]);

  const handleDelete = useCallback((id: string) => {
    setSelectedVenueType(prev => {
      const updated = prev.filter(item => item.id !== id);
      setValue(
        venueIdsField,
        updated.map(venue => venue.id),
      );
      return updated;
    });
  }, []);

  const handleChange = useCallback(
    (node: FilterVenueTypesNode) => {
      setSelectedVenueType(prev => {
        const isSelected = prev.some(selected => selected.id === node.id);

        if (isSelected) {
          return prev.filter(item => item.id !== node.id);
        }

        const children = getAllVenueTypesFilterTreeChildren(node);
        const newNodes = [node, ...children];
        const newIds = new Set(newNodes.map(item => item.id));

        const filteredPrev = prev.filter(item => !newIds.has(item.id));

        return [...filteredPrev, ...newNodes];
      });
      setShouldUpdate(true);
    },
    [selectedVenueType],
  );

  const renderChip = useCallback(
    (venueType: IDoohFilterVenueType) => (
      <Chip
        key={venueType.id}
        label={venueType.name}
        onDelete={() => handleDelete(venueType.id)}
        sx={{
          backgroundColor: 'transparent',
          border: '1px solid #DADADA',
          borderRadius: 32,
          height: 34,
          fontWeight: 700,
          fontSize: 12,
          '& .tv-filled-icon': {
            margin: '0 -8px -2px 15px',
            color: '#1B4332',
          },
          '& .delete-icon': {
            margin: '0 15px 0 -8px',
          },
        }}
      />
    ),
    [],
  );

  return (
    <Paper className='flex justify-start items-start rounded-lg flex-col break-after-page'>
      <VenueTypeHeader />
      <div className='p-6 flex flex-col w-full gap-2 '>
        <div className='text-sm font-medium'>Select Venue Type</div>
        <Box
          sx={{
            width: '100%',
            marginBottom: '8px',
          }}
        >
          <Button disableRipple onClick={handleClick}>
            <span>Select Venue</span>
            <ExpandMore sx={{ color: Color.Grey }} />
          </Button>
          <div className='flex gap-1 flex-wrap mt-[12px]'>
            {showAllChips
              ? selectedVenueType?.map(renderChip)
              : selectedVenueType?.slice(0, FORECASTER_DOOH_VENUE_TYPE_CHIPS_LIMIT).map(renderChip)}
            {!showAllChips && selectedVenueType?.length > FORECASTER_DOOH_VENUE_TYPE_CHIPS_LIMIT && (
              <Tooltip
                title={
                  <ul>
                    {selectedVenueType
                      ?.slice(
                        FORECASTER_DOOH_VENUE_TYPE_CHIPS_LIMIT,
                        FORECASTER_DOOH_VENUE_TYPE_CHIPS_LIMIT + TOOLTIP_LIST_LIMIT,
                      )
                      .map(({ id, name }) => (
                        <li key={id}>{name}</li>
                      ))}
                    {selectedVenueType?.length > FORECASTER_DOOH_VENUE_TYPE_CHIPS_LIMIT + TOOLTIP_LIST_LIMIT && (
                      <li>
                        and {selectedVenueType?.length - (FORECASTER_DOOH_VENUE_TYPE_CHIPS_LIMIT + TOOLTIP_LIST_LIMIT)}{' '}
                        others
                      </li>
                    )}
                  </ul>
                }
                placement='top'
                onClick={() => setShowAllChips(true)}
              >
                <div
                  className={`flex px-3 py-1 border border-[${'#DADADA'}] rounded-full cursor-default hover:border-[#1B4332] hover:bg-[#1b43320a]`}
                >
                  <span className={`text-[${PRIMARY}] text-[14px] font-semibold`}>
                    +{selectedVenueType?.length - FORECASTER_DOOH_VENUE_TYPE_CHIPS_LIMIT}
                  </span>
                </div>
              </Tooltip>
            )}
          </div>
        </Box>
        <StyledPopper
          open={open}
          anchorEl={anchorEl}
          sx={{
            width: anchorEl && anchorEl?.clientWidth > 300 ? '300px' : anchorEl?.clientWidth,
          }}
          placement='bottom-start'
          modifiers={[
            {
              name: 'offset',
              options: {
                offset: [0, 10],
              },
            },
          ]}
        >
          <ClickAwayListener onClickAway={handleClose}>
            <div>
              <VenueTypePanel nodes={venueTypesTree} selected={selectedVenueType} onChange={handleChange} />
            </div>
          </ClickAwayListener>
        </StyledPopper>
      </div>
    </Paper>
  );
};

export default VenueType;
